import { motion } from "framer-motion";
import { ArrowRight, Building2, Video, Send, EyeOff, Sparkles, Layers, Globe, Infinity as InfinityIcon } from "lucide-react";
import SceneShell from "../components/SceneShell";

/**
 * Scene 20 — Old World vs Your World.
 * Two parallel chains shown side-by-side. The right side dwarfs the left in
 * scale and brightness — visualising the multiplier.
 */

const OLD = [
  { icon: Building2, label: "1 property" },
  { icon: Video, label: "1 video" },
  { icon: Send, label: "1 post" },
  { icon: EyeOff, label: "Limited reach" },
];

const YOURS = [
  { icon: Building2, label: "1 property" },
  { icon: Layers, label: "10+ assets" },
  { icon: Globe, label: "Multi-platform" },
  { icon: InfinityIcon, label: "Constant presence" },
];

function Chain({ items, palette, dim = false, delay = 0 }) {
  return (
    <ul className="space-y-3">
      {items.map((it, i) => {
        const Icon = it.icon;
        const last = i === items.length - 1;
        return (
          <motion.li
            key={it.label}
            initial={{ opacity: 0, x: dim ? -16 : 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{
              delay: delay + i * 0.18,
              duration: 0.7,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="flex items-center gap-4"
          >
            <span
              className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
              style={{
                background: palette.iconBg,
                color: palette.icon,
                border: `1px solid ${palette.iconBorder}`,
              }}
            >
              <Icon className="w-5 h-5" />
            </span>
            <span
              className="text-lg sm:text-xl font-semibold"
              style={{ color: palette.text }}
            >
              {it.label}
            </span>
            {!last ? (
              <ArrowRight
                className="w-4 h-4 ml-auto"
                style={{ color: palette.arrow }}
              />
            ) : null}
          </motion.li>
        );
      })}
    </ul>
  );
}

export default function Scene20_OldVsYourWorld() {
  return (
    <SceneShell variant="ink" align="center" data-testid="scene-old-vs-your">
      <div className="relative max-w-[1500px] mx-auto w-full">
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="kn-display text-3xl sm:text-5xl lg:text-6xl text-white text-center mb-14"
          data-testid="old-vs-your-title"
        >
          One property.{" "}
          <span style={{ color: "#96C11F" }}>Different output.</span>
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* OLD WORLD */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl p-8 relative overflow-hidden"
            style={{
              background: "rgba(200, 58, 79, 0.06)",
              border: "1px solid rgba(200, 58, 79, 0.22)",
            }}
            data-testid="old-world-card"
          >
            <div
              className="text-[11px] tracking-[0.32em] uppercase mb-6"
              style={{ color: "#c83a4f" }}
            >
              Old World
            </div>
            <Chain
              items={OLD}
              palette={{
                iconBg: "rgba(200, 58, 79, 0.12)",
                iconBorder: "rgba(200, 58, 79, 0.32)",
                icon: "#c83a4f",
                text: "rgba(255,255,255,0.78)",
                arrow: "rgba(200, 58, 79, 0.55)",
              }}
              dim
              delay={0.7}
            />
          </motion.div>

          {/* YOUR WORLD */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl p-8 relative overflow-hidden glass"
            style={{
              boxShadow: "0 0 80px rgba(150,193,31,0.18)",
            }}
            data-testid="your-world-card"
          >
            <motion.div
              className="absolute inset-0 pointer-events-none"
              animate={{ opacity: [0.5, 0.85, 0.5] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              style={{
                background:
                  "radial-gradient(ellipse at 80% 20%, rgba(150,193,31,0.18) 0%, transparent 60%)",
              }}
            />
            <div className="relative">
              <div className="flex items-center gap-2 mb-6">
                <Sparkles className="w-3.5 h-3.5" style={{ color: "#96C11F" }} />
                <div
                  className="text-[11px] tracking-[0.32em] uppercase"
                  style={{ color: "#96C11F" }}
                >
                  Your World
                </div>
              </div>
              <Chain
                items={YOURS}
                palette={{
                  iconBg: "rgba(150, 193, 31, 0.18)",
                  iconBorder: "rgba(150, 193, 31, 0.45)",
                  icon: "#96C11F",
                  text: "#e2e8f0",
                  arrow: "rgba(0, 222, 182, 0.85)",
                }}
                delay={1.0}
              />
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ delay: 2.6, duration: 0.8 }}
          className="mt-12 inline-flex items-center gap-2 text-[11px] tracking-[0.32em] uppercase text-white/55 mx-auto justify-center w-full"
        >
          Same listing · 10× the output · 0× the effort
        </motion.div>
      </div>
    </SceneShell>
  );
}
